import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import VarList from '../Components/VarList';
import FilterModal from '../Components/FilterModal';
import api from '../services/api';

const inp = (extra = {}) => ({
  width: '100%', padding: '8px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8,
  fontSize: 13, outline: 'none', color: '#334155', background: '#fff',
  fontFamily: 'inherit', boxSizing: 'border-box', ...extra,
});
const labelCss = { fontSize: 11.5, fontWeight: 700, color: '#64748b', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };
const btn = (bg, color = '#fff', extra = {}) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, color,
  border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', ...extra,
});

const CrossTab = () => {
  const [crossTabData, setCrossTabData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [firstDropdownValue, setFirstDropdownValue] = useState('');
  const [secondDropdownValue, setSecondDropdownValue] = useState('');
  const [firstFilterText, setFirstFilterText] = useState('');
  const [secondFilterText, setSecondFilterText] = useState('');
  const [filterLogicData, setFilterLogicData] = useState(null);
  const [radioButton, setRadioButton] = useState('Percentage');
  const [filterNames, setFilterNames] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [savedDataName, setSavedDataName] = useState('');

  const handleNameChange = (e) => setSavedDataName(e.target.value);

  const downloadExcel = () => {
    const dataArray = [];
    for (const rowKey in filteredData) {
      const row = { RowKey: rowKey, ...filteredData[rowKey] };
      dataArray.push(row);
    }
    const ws = XLSX.utils.json_to_sheet(dataArray);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CrossTab Data');
    const wscols = [{ wch: 20 }, { wch: 30 }, { wch: 40 }];
    ws['!cols'] = wscols;
    XLSX.writeFile(wb, 'CrossTabData.xlsx');
  };

  const handleRemoveFilter = (filterNameToRemove) => {
    setFilterNames(filterNames.filter((f) => f.name !== filterNameToRemove));
    setSelectedFilter(null);
    setFilterLogicData(null);
  };

  useEffect(() => {
    const fetchFilterNames = async () => {
      try {
        const response = await api.get('filter/filter-names/', {
          headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
        });
        setFilterNames(response.data);
      } catch (error) {
        console.error('Error fetching filter names:', error);
      }
    };
    fetchFilterNames();
  }, []);

  const handleSelectedFilter = (selectedItem) => setFilterLogicData(selectedItem.filter_name);

  const handleFilterSelection = (filter) => {
    setSelectedFilter(filter);
    setFilterLogicData(filter.filterLogic ? filter.filter_name : null);
  };

  useEffect(() => {
    const savedFirst = localStorage.getItem('firstDropdownValue');
    const savedSecond = localStorage.getItem('secondDropdownValue');
    const savedRadio = localStorage.getItem('radioButton');
    const savedFilter = localStorage.getItem('selectedFilter');
    if (savedFirst) setFirstDropdownValue(savedFirst);
    if (savedSecond) setSecondDropdownValue(savedSecond);
    if (savedRadio) setRadioButton(savedRadio);
    if (savedFilter) setSelectedFilter(JSON.parse(savedFilter));
  }, []);

  useEffect(() => {
    localStorage.setItem('firstDropdownValue', firstDropdownValue);
    localStorage.setItem('secondDropdownValue', secondDropdownValue);
    localStorage.setItem('radioButton', radioButton);
    if (selectedFilter) localStorage.setItem('selectedFilter', JSON.stringify(selectedFilter));
    else localStorage.removeItem('selectedFilter');
  }, [firstDropdownValue, secondDropdownValue, radioButton, selectedFilter]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('crosstab/crosstab/', {
          params: {
            parameter1: firstDropdownValue,
            parameter2: secondDropdownValue,
            parameter3: filterLogicData || null,
            parameter4: radioButton,
          },
          headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
        });
        const parsedData = JSON.parse(response.data.cross_tab_data);
        setCrossTabData(parsedData);
        setFilteredData(parsedData);
      } catch (error) {
        console.error('Error fetching cross-tab data:', error);
        setCrossTabData([]);
        setFilteredData([]);
      }
    };
    if (firstDropdownValue && secondDropdownValue) fetchData();
  }, [firstDropdownValue, secondDropdownValue, filterLogicData, radioButton]);

  useEffect(() => {
    if (firstDropdownValue && secondDropdownValue) {
      setSavedDataName(`${firstDropdownValue} X ${secondDropdownValue}`);
    }
  }, [firstDropdownValue, secondDropdownValue]);

  const handleSave = async () => {
    try {
      await api.post('savetabs/', {
        name: savedDataName,
        SideBreak: firstDropdownValue,
        TopBreak: secondDropdownValue,
        filterLogicData: filterLogicData || null,
        radioButton,
      });
      alert('Data saved successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Error saving data. Please try again later.');
    }
  };

  const colKeys = filteredData && Object.keys(filteredData).length > 0
    ? Object.keys(Object.values(filteredData)[0])
    : [];
  const rowEntries = filteredData ? Object.entries(filteredData) : [];
  const filtersDisabled = filterNames.length === 0 || !firstDropdownValue || !secondDropdownValue;

  return (
    <div style={{ padding: '24px 28px', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ background: '#0f172a', borderRadius: 12, padding: '18px 24px', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>Cross Tabulation</div>
          <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>Side break × top break analysis</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={downloadExcel} disabled={rowEntries.length === 0} style={btn('#16a34a', '#fff', { opacity: rowEntries.length === 0 ? 0.5 : 1, cursor: rowEntries.length === 0 ? 'not-allowed' : 'pointer' })}>
            ↓ Excel
          </button>
          <button onClick={handleSave} disabled={!firstDropdownValue || !secondDropdownValue} style={btn('#3b82f6', '#fff', { opacity: !firstDropdownValue || !secondDropdownValue ? 0.5 : 1, cursor: !firstDropdownValue || !secondDropdownValue ? 'not-allowed' : 'pointer' })}>
            ✓ Save
          </button>
        </div>
      </div>

      {/* Controls card */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {/* Side Break */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelCss}>Side Break</label>
            <input type="text" placeholder="Search…" value={firstFilterText} onChange={(e) => setFirstFilterText(e.target.value)} style={inp({ marginBottom: 8 })} />
            <select value={firstDropdownValue} onChange={(e) => setFirstDropdownValue(e.target.value)} style={inp()}>
              <VarList filterText={firstFilterText} />
            </select>
          </div>
          {/* Top Break */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={labelCss}>Top Break</label>
            <input type="text" placeholder="Search…" value={secondFilterText} onChange={(e) => setSecondFilterText(e.target.value)} style={inp({ marginBottom: 8 })} />
            <select value={secondDropdownValue} onChange={(e) => setSecondDropdownValue(e.target.value)} style={inp()}>
              <VarList filterText={secondFilterText} />
            </select>
          </div>
          {/* Filter */}
          <div style={{ minWidth: 200 }}>
            <label style={labelCss}>Saved Filter</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                disabled={filtersDisabled}
                value={selectedFilter?.filter_name || ''}
                onChange={(e) => {
                  if (!e.target.value) { handleRemoveFilter(selectedFilter?.filter_name); return; }
                  const f = filterNames.find((fn) => fn.filter_name === e.target.value);
                  if (f) { handleFilterSelection(f); handleSelectedFilter(f); }
                }}
                style={inp({ width: 'auto', minWidth: 160 })}
              >
                <option value="">— Apply Filter —</option>
                {filterNames.map((f, i) => <option key={i} value={f.filter_name}>{f.filter_name}</option>)}
              </select>
              {selectedFilter && (
                <button onClick={() => handleRemoveFilter(selectedFilter.filter_name)} style={btn('#f43f5e', '#fff', { padding: '7px 10px', fontSize: 12 })}>✕</button>
              )}
            </div>
          </div>
        </div>

        {/* Display mode radios */}
        <div style={{ marginTop: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Display:</span>
          {['Percentage', 'Absolute'].map((mode) => (
            <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#334155' }}>
              <input type="radio" name="radioMode" checked={radioButton === mode} onChange={() => setRadioButton(mode)} style={{ accentColor: '#0f172a' }} />
              {mode}
            </label>
          ))}
        </div>
      </div>

      {/* Results table */}
      {firstDropdownValue && secondDropdownValue && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px' }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
            {firstDropdownValue} × {secondDropdownValue}
            {selectedFilter && <span style={{ marginLeft: 8, fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic' }}>({selectedFilter.filter_name})</span>}
          </div>
          {rowEntries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>No data available for this combination.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table id="table-to-xls" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 700, fontSize: 11.5, color: '#475569', whiteSpace: 'nowrap' }}>
                      {firstDropdownValue}
                    </th>
                    {colKeys.map((key, i) => (
                      <th key={i} style={{ padding: '10px 12px', textAlign: 'center', border: '1px solid #e2e8f0', fontWeight: 700, fontSize: 11.5, color: '#475569', whiteSpace: 'nowrap' }}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rowEntries.map(([rowKey, rowData], rowIndex) => (
                    <tr key={rowKey} style={{ background: rowIndex % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>{rowKey}</td>
                      {Object.values(rowData).map((value, i) => (
                        <td key={i} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'center', color: '#475569', fontVariantNumeric: 'tabular-nums' }}>{value}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!firstDropdownValue && !secondDropdownValue && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '48px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          Select a side break and top break variable above to generate the cross tabulation.
        </div>
      )}

      <div>
        <FilterModal />
      </div>
    </div>
  );
};

export default CrossTab;
